import { useEffect, useState } from "react";
import { api, formatError, fmtINR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Check, Trash2 } from "lucide-react";

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", price: 0, duration_days: 30, description: "", features: "" });

  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/admin/plans", { ...f, features: f.features.split("\n").filter(Boolean) });
      toast.success("Plan created"); setOpen(false); setF({ name: "", price: 0, duration_days: 30, description: "", features: "" }); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => { await api.delete(`/admin/plans/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="admin-plans-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Subscription plans</h1><p className="text-slate-500">Manage what you offer store owners</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-orange-500 hover:bg-orange-600" data-testid="btn-create-plan"><Plus className="w-4 h-4 mr-2" />New Plan</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create plan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price (₹)</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: +e.target.value })} /></div>
                <div><Label>Duration (days)</Label><Input type="number" value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: +e.target.value })} /></div>
              </div>
              <div><Label>Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
              <div><Label>Features (one per line)</Label><textarea rows={4} className="w-full text-sm border rounded-md p-2" value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={submit} className="bg-orange-500 hover:bg-orange-600">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <Card key={p.id} className="border-slate-200" data-testid={`plan-${p.name}`}>
            <CardContent className="p-6 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-orange-600 font-semibold">{p.name}</div>
                <div className="font-display text-4xl font-black mt-1">{fmtINR(p.price)}<span className="text-sm text-slate-400 font-normal">/{p.duration_days}d</span></div>
                <div className="text-sm text-slate-500 mt-1">{p.description}</div>
              </div>
              <ul className="space-y-1.5 text-sm">
                {(p.features || []).map((ft, i) => <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5" /><span>{ft}</span></li>)}
              </ul>
              <Button variant="ghost" size="sm" onClick={() => del(p.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-plan-${p.name}`}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
